import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, X, CalendarDays, Info, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useBreakpoint } from "@/hooks/useBreakpoint";

export const BLOCK_REASONS = [
  ["renovation", "Renovation / Repairs"], ["deep_cleaning", "Deep Cleaning"], ["vip_hold", "VIP Hold"], ["management_hold", "Management Hold"],
  ["inspection", "Inspection / Photography"], ["owner_use", "Owner's Use"], ["other", "Other"],
];
const todayISO = () => new Date().toLocaleDateString("en-CA");
const addDays = (d, n) => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };
const nights = (a, b) => (a && b ? Math.max(0, Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000)) : 0);
const D = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");
const DY = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const inputCls = "h-10 w-full rounded-[6px] border border-[#D5E6E8] bg-white px-3 text-[13px] text-[#1A2425] outline-none focus:border-[#37F4B5] focus:shadow-[0_0_0_3px_rgba(55,244,181,0.15)]";

/** Block Room modal (desktop/tablet) / bottom sheet (mobile) — ROOM-BLOCK-1. */
export function BlockRoomModal({ room, open, onOpenChange, onBlocked }) {
  const bp = useBreakpoint();
  const navigate = useNavigate();
  const [from, setFrom] = useState(todayISO);
  const [to, setTo] = useState(() => addDays(todayISO(), 1));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [conflicts, setConflicts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => { if (open) { setFrom(todayISO()); setTo(addDays(todayISO(), 1)); setReason(""); setNote(""); setConflicts(null); } }, [open]);

  const n = nights(from, to);
  const dateErr = from && to && to <= from ? "End date must be after start date" : null;
  const future = from > todayISO();

  useEffect(() => {
    if (!open || !room?.room_id || !from || !to || dateErr) { setConflicts(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setChecking(true);
      try { const { data } = await api.get(`/rooms/${room.room_id}/check-availability`, { params: { check_in: from, check_out: to } }); setConflicts(data.conflicts || []); }
      catch { setConflicts([]); }
      setChecking(false);
    }, 400);
    return () => clearTimeout(timer.current);
  }, [open, room?.room_id, from, to, dateErr]);

  const hasConflict = (conflicts || []).length > 0;
  const canSubmit = !!from && !!to && !dateErr && !!reason && !hasConflict && !checking && !saving;
  const resConflict = useMemo(() => (conflicts || []).find((c) => c.type === "reservation" || c.type === "stay"), [conflicts]);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/rooms/${room.room_id}/blocks`, { block_from: from, block_to: to, reason, reason_note: note.trim() || null, hotel_id: room.hotel_id });
      toast.success(data.status === "scheduled" ? `Room ${room.room_number} block scheduled from ${DY(from)}` : `Room ${room.room_number} blocked until ${DY(to)}`);
      onOpenChange(false); onBlocked?.(data);
    } catch (e) {
      const body = e?.response?.data || {};
      const d = body.detail && typeof body.detail === "object" ? body.detail : body;
      if (d?.conflicts) setConflicts(d.conflicts); else toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const body = (
    <>
      <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3 border-b border-[#E6EFF0]">
        <div>
          <div className="flex items-center gap-2 text-[16px] font-bold text-[#1A2425]"><Lock size={20} strokeWidth={1.5} color="#3A4A5A" />Block Room {room?.room_number}</div>
          <div className="text-[12px] text-[#3F5257] mt-0.5">This room will be unavailable for booking during the selected period.</div>
        </div>
        <button type="button" onClick={() => onOpenChange(false)} className="w-8 h-8 -mr-2 flex items-center justify-center rounded-lg hover:bg-[#F4FCFC]" aria-label="Close" data-testid="block-close"><X size={18} strokeWidth={1.5} color="#97A6A9" /></button>
      </div>
      <div className="p-5 space-y-4 flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="block text-[12px] text-[#3F5257] mb-1">Block From <span className="text-[#DC2626]">*</span></span>
            <input type="date" value={from} min={todayISO()} onChange={(e) => { setFrom(e.target.value); if (to && e.target.value && to <= e.target.value) setTo(addDays(e.target.value, 1)); }} className={inputCls} data-testid="block-from" /></label>
          <label className="block"><span className="block text-[12px] text-[#3F5257] mb-1">Block To <span className="text-[#DC2626]">*</span></span>
            <input type="date" value={to} min={from ? addDays(from, 1) : todayISO()} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="block-to" /></label>
        </div>
        {dateErr ? <div className="text-[12px] text-[#991B1B]" data-testid="block-date-error">{dateErr}</div> : n > 0 && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#E6FEFF] px-3 py-1 text-[12px] font-bold text-[#014751]" data-testid="block-duration"><CalendarDays size={14} strokeWidth={1.5} />{n} night{n === 1 ? "" : "s"} · {D(from)} → {DY(to)}</div>
        )}
        <div>
          <span className="block text-[12px] text-[#3F5257] mb-1">Reason <span className="text-[#DC2626]">*</span></span>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="h-10 rounded-[6px] border-[#D5E6E8] text-[13px]" data-testid="block-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
            <SelectContent>{BLOCK_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {!reason && <div className="text-[11px] text-[#97A6A9] mt-1">Please select a reason</div>}
        </div>
        <label className="block"><span className="block text-[12px] text-[#3F5257] mb-1">Additional Notes <span className="text-[#97A6A9]">(optional)</span></span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Add any notes about this block..." className={`${inputCls} h-[72px] py-2 resize-none`} data-testid="block-note" /></label>
        {future && !dateErr && (
          <div className="rounded-r-[6px] border-l-[3px] border-[#014751] bg-[#E6FEFF] px-3.5 py-2.5 text-[13px] text-[#014751] flex items-start gap-2" data-testid="block-future-banner">
            <Info size={16} strokeWidth={1.5} className="shrink-0 mt-0.5" />This is a scheduled block. Room stays available until {DY(from)}.
          </div>
        )}
        {hasConflict && (
          <div className="rounded-r-[6px] border-l-[3px] border-[#DC2626] bg-[#FEE2E2] px-3.5 py-3" data-testid="block-conflict-banner">
            <div className="flex items-center gap-2 text-[13px] font-bold text-[#991B1B]"><AlertTriangle size={16} strokeWidth={1.5} color="#DC2626" />{resConflict ? "Reservation conflict" : "Existing block overlaps"}</div>
            <div className="text-[12px] text-[#991B1B] leading-relaxed mt-1">
              {resConflict
                ? `Room has an active ${resConflict.type === "stay" ? "stay" : "reservation"} from ${D(resConflict.from)} → ${D(resConflict.to)} (${resConflict.guest_name || resConflict.code}). Please cancel or move this ${resConflict.type === "stay" ? "guest" : "reservation"} before blocking.`
                : `Another ${conflicts[0].status} block covers ${D(conflicts[0].from)} → ${D(conflicts[0].to)} (${conflicts[0].reason_label}). Choose non-overlapping dates.`}
            </div>
            {resConflict && <button type="button" onClick={() => { onOpenChange(false); navigate(resConflict.type === "stay" ? `/pms/stays/${resConflict.id}` : `/pms/reservations/${resConflict.id}`); }} className="mt-1.5 text-[12px] font-bold text-[#DC2626] hover:underline" data-testid="block-view-conflict">View {resConflict.type === "stay" ? "Stay" : "Reservation"} →</button>}
          </div>
        )}
      </div>
      <div className="border-t border-[#E6EFF0] px-5 py-4 flex justify-end gap-2">
        <button type="button" onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-[6px] border border-[#D5E6E8] text-[13px] text-[#3F5257]" data-testid="block-cancel">Cancel</button>
        <button type="button" onClick={submit} disabled={!canSubmit} data-testid="block-submit" style={{ color: canSubmit ? "#FFFFFF" : "#97A6A9" }}
          className={`h-10 px-4 rounded-[6px] text-[13px] font-bold flex items-center gap-2 ${canSubmit ? "bg-[#1A2425] hover:bg-[#014751]" : "bg-[#E5E7EB] cursor-not-allowed"}`}>
          <Lock size={16} strokeWidth={1.5} color={canSubmit ? "#FFFFFF" : "#97A6A9"} />{saving ? "Blocking…" : checking ? "Checking…" : "Block Room"}
        </button>
      </div>
    </>
  );

  if (bp === "mobile") {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" hideCloseButton className="p-0 h-[90vh] rounded-t-2xl flex flex-col bg-white border-t-[3px] border-[#1A2425]" data-testid="block-room-modal">
          <div className="pt-3 flex justify-center"><div className="w-10 h-1 rounded-full bg-[#D5E6E8]" /></div>
          <SheetTitle className="sr-only">Block Room</SheetTitle><SheetDescription className="sr-only">Block room for a date range</SheetDescription>
          {body}
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-[520px] rounded-xl border-0 border-t-[3px] border-[#1A2425] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)] flex flex-col max-h-[90vh] [&>button]:hidden" data-testid="block-room-modal">
        <DialogTitle className="sr-only">Block Room</DialogTitle><DialogDescription className="sr-only">Block room for a date range</DialogDescription>
        {body}
      </DialogContent>
    </Dialog>
  );
}
